import type { CatalogRole } from '../../../services/iam';

// Builds the include TREE for RoleFlow (not the flattened list); each node
// keeps only the permissions it declares itself (`own`).
interface RoleFlowNodeShared {
    readonly key: string;
    readonly desc: string;
    readonly own: readonly string[];
    readonly children: readonly RoleFlowNode[];
}

export interface RoleFlowNodeRole extends RoleFlowNodeShared {
    readonly kind: 'role';
    readonly title: string;
}

export interface RoleFlowNodeBundle extends RoleFlowNodeShared {
    readonly kind: 'bundle';
}

export type RoleFlowNode = RoleFlowNodeRole | RoleFlowNodeBundle;

/** Cycle-safe walk of a role's `includes` graph (mirrors backend flattenRole's `seen` guard). */
export function buildRoleFlow(
    rootKey: string,
    rolesByKey: ReadonlyMap<string, CatalogRole>
): RoleFlowNode | null {
    const build = (key: string, seen: ReadonlySet<string>): RoleFlowNode | null => {
        const role = rolesByKey.get(key);
        if (!role || seen.has(key)) return null;
        const nextSeen = new Set(seen).add(key);
        const children = role.includes
            .map((childKey) => build(childKey, nextSeen))
            .filter((node): node is RoleFlowNode => node !== null);
        if (role.kind === 'bundle') {
            return { key: role.key, kind: 'bundle', desc: role.desc, own: role.own, children };
        }
        return {
            key: role.key,
            kind: 'role',
            title: role.title,
            desc: role.desc,
            own: role.own,
            children
        };
    };

    return build(rootKey, new Set());
}

/** Roles that include this one, directly - feeds Roles' "included by" panel. */
export function findIncludedBy(
    roleKey: string,
    roles: readonly CatalogRole[]
): readonly CatalogRole[] {
    return roles.filter((role) => role.includes.includes(roleKey));
}
